/**
 * Opus Tool Activity - Shows running/completed tool calls
 */

import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import type { OpusToolCall } from '@/stores/opus-ai-store';

interface OpusToolActivityProps {
  toolCalls?: OpusToolCall[];
  activeTools?: Array<{ name: string; startTime: number }>;
}

// Human-readable tool names
const TOOL_LABELS: Record<string, string> = {
  search_candidates: 'Searching candidates',
  get_candidate: 'Getting candidate details',
  update_candidate: 'Updating candidate',
  add_tags: 'Adding tags',
  search_jobs: 'Searching jobs',
  create_job: 'Creating job',
  assign_to_job: 'Assigning to job',
  move_pipeline_stage: 'Moving pipeline stage',
  get_pipeline: 'Getting pipeline',
  add_note: 'Adding note',
  get_dashboard_stats: 'Getting statistics',
  match_candidates_to_job: 'Matching candidates',
};

function getToolLabel(name: string): string {
  return TOOL_LABELS[name] || name.replace(/_/g, ' ');
}

export function OpusToolActivity({ toolCalls, activeTools }: OpusToolActivityProps) {
  const hasActivity = (toolCalls && toolCalls.length > 0) || (activeTools && activeTools.length > 0);

  if (!hasActivity) {
    return null;
  }

  return (
    <div className="space-y-1.5 py-2">
      {/* Active tools (running) */}
      {activeTools?.map((tool) => (
        <div
          key={`${tool.name}-${tool.startTime}`}
          className="flex items-center gap-2 text-xs text-text-tertiary"
        >
          <Loader2 className="w-3 h-3 animate-spin text-emerald-500" />
          <span>{getToolLabel(tool.name)}...</span>
        </div>
      ))}

      {/* Completed tool calls */}
      {toolCalls?.map((tool, index) => (
        <div
          key={index}
          className="flex items-center gap-2 text-xs text-text-tertiary"
        >
          {tool.success ? (
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          ) : (
            <XCircle className="w-3 h-3 text-error-500" />
          )}
          <span>{getToolLabel(tool.name)}</span>
          <span className="text-text-tertiary/60">
            {tool.duration}ms
          </span>
        </div>
      ))}
    </div>
  );
}
